// Pure date maths of the calendar: month grid (weeks Monday → Sunday), week range, the period the
// API is asked for, navigation (prev/next, moving the focused day) and period titles. Local time of
// the browser, DST-safe (local Date constructors, never "+ 24 h"). DOM-free.
import { MONTHS, MONTHS_SHORT, DAYS } from '../../i18n/fr.js';

/** Largest range GET /api/calendar accepts (SPEC §15). */
export const MAX_RANGE_DAYS = 62;

const pad2 = (n) => String(n).padStart(2, '0');

/** 00:00 of the local day of `ms`. */
export function startOfDay(ms) {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Same wall-clock day shifted by `n` days (00:00 of that day). */
export function addDays(ms, n) {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n).getTime();
}

/** 00:00 of the Monday of the week of `ms`. */
export function startOfWeek(ms) {
  const d = new Date(ms);
  const offset = (d.getDay() + 6) % 7; // Monday = 0
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - offset).getTime();
}

/** 00:00 of the first day of the month of `ms`. */
export function startOfMonth(ms) {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), 1).getTime();
}

/** 'YYYY-MM-DD' of the local day of `ms`. */
export function dayKey(ms) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** 00:00 of a 'YYYY-MM-DD' key (null when invalid). */
export function keyToMs(key) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key ?? ''));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.getMonth() === Number(m[2]) - 1 ? d.getTime() : null;
}

/** True when both instants fall on the same local day. */
export function sameDay(a, b) {
  return dayKey(a) === dayKey(b);
}

/**
 * A day cell of the grids.
 * @typedef {{key: string, ms: number, date: number, month: number, year: number, weekday: number,
 *   weekend: boolean, inMonth: boolean, today: boolean, past: boolean, first: boolean}} Day
 */

function makeDay(ms, month, now) {
  const d = new Date(ms);
  const today = startOfDay(now);
  return {
    key: dayKey(ms), ms, date: d.getDate(), month: d.getMonth(), year: d.getFullYear(), weekday: d.getDay(),
    weekend: d.getDay() === 0 || d.getDay() === 6, inMonth: month == null || d.getMonth() === month,
    today: ms === today, past: ms < today, first: d.getDate() === 1,
  };
}

/**
 * The month grid around `anchorMs`: whole weeks (Monday → Sunday) covering the month (4 to 6 rows).
 * @param {number} anchorMs any instant of the month
 * @param {{now?: number}} [opts]
 * @returns {{from: number, to: number, weeks: Day[][], month: number, year: number}}
 */
export function monthGrid(anchorMs, { now = Date.now() } = {}) {
  const first = startOfMonth(anchorMs);
  const d = new Date(first);
  const month = d.getMonth();
  const lastDay = new Date(d.getFullYear(), month + 1, 0).getTime();
  const from = startOfWeek(first);
  const to = addDays(startOfWeek(lastDay), 7);
  const weeks = [];
  for (let day = from; day < to; day = addDays(day, 7)) {
    const week = [];
    for (let i = 0; i < 7; i += 1) week.push(makeDay(addDays(day, i), month, now));
    weeks.push(week);
  }
  return { from, to, weeks, month, year: d.getFullYear() };
}

/**
 * The week (Monday → Sunday) of `anchorMs`.
 * @returns {{from: number, to: number, days: Day[]}}
 */
export function weekGrid(anchorMs, { now = Date.now() } = {}) {
  const from = startOfWeek(anchorMs);
  const days = [];
  for (let i = 0; i < 7; i += 1) days.push(makeDay(addDays(from, i), null, now));
  return { from, to: addDays(from, 7), days };
}

/**
 * The range [from, to) shown by a view (what GET /api/calendar is asked for).
 * @param {'month'|'week'} view
 * @param {number} anchorMs
 */
export function viewRange(view, anchorMs) {
  if (view === 'week') {
    const from = startOfWeek(anchorMs);
    return { from, to: addDays(from, 7) };
  }
  const { from, to } = monthGrid(anchorMs);
  return { from, to };
}

/**
 * The anchor after prev/next: ±1 month (day clamped to the month's length) or ±7 days.
 * @param {'month'|'week'} view
 * @param {number} anchorMs
 * @param {number} delta -1 or +1 (or more)
 */
export function shiftAnchor(view, anchorMs, delta) {
  if (view === 'week') return addDays(anchorMs, 7 * delta);
  const d = new Date(anchorMs);
  const target = new Date(d.getFullYear(), d.getMonth() + delta, 1);
  const days = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  return new Date(target.getFullYear(), target.getMonth(), Math.min(d.getDate(), days)).getTime();
}

/** Arrow key → day offset for the focused day (null for other keys). */
export function arrowDelta(key) {
  return { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[key] ?? null;
}

/** True when `ms` lies in [from, to). */
export function inRange(ms, { from, to }) {
  return ms >= from && ms < to;
}

/**
 * Title of the period: month view "septembre" + "2026"; week view "21 – 27 sept." + "2026"
 * (months shown on both sides when the week spans two).
 * @param {'month'|'week'} view
 * @param {number} anchorMs
 * @returns {{main: string, year: string, label: string}} label: accessible full text
 */
export function periodTitle(view, anchorMs) {
  if (view === 'week') {
    const from = new Date(startOfWeek(anchorMs));
    const to = new Date(addDays(from.getTime(), 6));
    const sameMonth = from.getMonth() === to.getMonth();
    const main = sameMonth
      ? `${from.getDate()} – ${to.getDate()} ${MONTHS_SHORT[to.getMonth()]}`
      : `${from.getDate()} ${MONTHS_SHORT[from.getMonth()]} – ${to.getDate()} ${MONTHS_SHORT[to.getMonth()]}`;
    const year = from.getFullYear() === to.getFullYear() ? String(to.getFullYear()) : `${from.getFullYear()}–${to.getFullYear()}`;
    const label = `Semaine du ${from.getDate()} ${MONTHS[from.getMonth()]} au ${to.getDate()} ${MONTHS[to.getMonth()]} ${to.getFullYear()}`;
    return { main, year, label };
  }
  const d = new Date(anchorMs);
  return { main: MONTHS[d.getMonth()], year: String(d.getFullYear()), label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}` };
}

/** "samedi 26 septembre 2026" (1er for the first). */
export function longDayLabel(ms) {
  const d = new Date(ms);
  const day = d.getDate() === 1 ? '1er' : String(d.getDate());
  return `${DAYS[d.getDay()]} ${day} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "sam. 26 sept." (short, no year). */
export function shortDayLabel(ms) {
  const d = new Date(ms);
  const day = d.getDate() === 1 ? '1er' : String(d.getDate());
  const wd = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'][d.getDay()];
  return `${wd} ${day} ${MONTHS_SHORT[d.getMonth()]}`;
}

/**
 * The instant for a day and a "HH:MM" time (local), or null when the time is invalid.
 * @param {number} dayMs any instant of the day
 * @param {string} hhmm
 */
export function atTime(dayMs, hhmm) {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(String(hhmm ?? ''));
  if (!m) return null;
  const d = new Date(dayMs);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), Number(m[1]), Number(m[2])).getTime();
}

/**
 * Default time proposed when scheduling on a day: 09:00, or the next full hour today (capped 23:00).
 * @param {number} dayMs
 * @param {number} [now]
 */
export function defaultTimeFor(dayMs, now = Date.now()) {
  if (!sameDay(dayMs, now)) return '09:00';
  const d = new Date(now);
  const h = Math.min(23, d.getHours() + 1);
  return `${pad2(h)}:00`;
}
